import { Link } from "react-router-dom";
import { Heart, Scales, ArrowRight } from "@phosphor-icons/react";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useCompare } from "@/context/CompareContext";
import { useCurrency } from "@/context/CurrencyContext";
import StarRating from "@/components/StarRating";

export default function ProductCard({ product, index = 0 }) {
  const { add } = useCart();
  const wishlist = useWishlist();
  const compare = useCompare();
  const { fmt } = useCurrency();
  const onSale = product.sale_price && product.sale_price < product.price;
  const link = `/product/${product.slug}`;

  return (
    <div
      className="group relative flex flex-col rounded-3xl border border-border bg-card overflow-hidden transition-all duration-500 hover:-translate-y-1.5 hover:shadow-soft-lg"
      data-testid={`product-card-${product.slug}`}
    >
      <Link to={link} className="relative block overflow-hidden aspect-[4/3] bg-muted">
        <img
          src={product.thumbnail}
          alt={`${product.name} electric bike`}
          loading={index < 4 ? "eager" : "lazy"}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute left-3 top-3 flex flex-wrap gap-2">
          {onSale && (
            <span className="rounded-full bg-primary px-3 py-1 text-xs font-bold text-primary-foreground" data-testid={`discount-badge-${product.slug}`}>
              -{Math.round((1 - product.sale_price / product.price) * 100)}%
            </span>
          )}
          {product.badge && (
            <span className="rounded-full bg-foreground px-3 py-1 text-xs font-bold text-background" data-testid={`product-badge-${product.slug}`}>{product.badge}</span>
          )}
          {product.is_featured && (
            <span className="rounded-full glass px-3 py-1 text-xs font-bold">★ Featured</span>
          )}
        </div>
        <div className="absolute right-3 top-3 flex flex-col gap-2 opacity-0 translate-x-2 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0">
          <button
            onClick={(e) => { e.preventDefault(); wishlist.toggle(product); }}
            className="grid h-9 w-9 place-items-center rounded-full glass hover:text-primary"
            aria-label="Add to wishlist"
            data-testid={`wishlist-btn-${product.slug}`}
          >
            <Heart size={18} weight={wishlist.has(product.slug) ? "fill" : "regular"}
              className={wishlist.has(product.slug) ? "text-primary" : ""} />
          </button>
          <button
            onClick={(e) => { e.preventDefault(); compare.toggle(product); }}
            className="grid h-9 w-9 place-items-center rounded-full glass hover:text-primary"
            aria-label="Add to compare"
            data-testid={`compare-btn-${product.slug}`}
          >
            <Scales size={18} weight={compare.has(product.slug) ? "fill" : "regular"}
              className={compare.has(product.slug) ? "text-primary" : ""} />
          </button>
        </div>
      </Link>

      <div className="flex flex-1 flex-col p-5">
        <span className="overline">{product.brand}</span>
        <Link to={link} className="mt-1.5 text-lg font-bold leading-tight tracking-tight hover:text-primary transition-colors">
          {product.name}
        </Link>
        <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{product.short_description}</p>
        <div className="mt-2">
          <StarRating value={product.rating} count={product.review_count} />
        </div>
        <div className="mt-auto flex items-end justify-between pt-4">
          <div>
            {onSale ? (
              <div className="flex items-baseline gap-2">
                <span className="text-xl font-extrabold text-primary">{fmt(product.sale_price)}</span>
                <span className="text-sm text-muted-foreground line-through">{fmt(product.price)}</span>
              </div>
            ) : (
              <span className="text-xl font-extrabold">{fmt(product.price)}</span>
            )}
          </div>
          <button
            onClick={() => add(product)}
            className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-4 py-2 text-sm font-semibold text-background transition-all hover:bg-primary hover:text-primary-foreground active:scale-95"
            data-testid={`add-to-cart-${product.slug}`}
          >
            Add <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
